import { ensureAuthenticated } from '@lune/auth/ensure'
import { ErrorPage } from '@lune/common/errors'
import { printRouterTree } from '@lune/common/router'
import React from 'react'
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { version } from './api/version'
import { Dashboard } from './components/dashboard'
import {
  AgentsRoute,
  ApplyRoute,
  DefaultPage,
  DisksRoute,
  LegacyFancyLoginPage,
  LegacyLoginFailed,
  LegacyLoginSuccess,
  LoginFailedPage,
  LoginPage,
  LoginSuccessPage,
  MachineDetailRoute,
  MachinesRoute,
  Root,
  UpdatesRoute
} from './paths'

const Machines = React.lazy(
  async () =>
    await import('./pages/machines').then((module) => ({
      default: module.MachinesPage
    }))
)
const MachinesDisks = React.lazy(
  async () =>
    await import('./pages/machinesDisks').then((module) => ({
      default: module.MachinesDisksPage
    }))
)
const MachineDetail = React.lazy(
  async () =>
    await import('./pages/machineDetail').then((module) => ({
      default: module.MachineDetailPage
    }))
)
const PageLogin = React.lazy(
  async () =>
    await import('./pages/login').then((module) => ({
      default: module.LoginPage
    }))
)
const Apply = React.lazy(
  async () =>
    await import('./pages/apply').then((module) => ({
      default: module.ApplyPage
    }))
)
const Updates = React.lazy(
  async () =>
    await import('./pages/updates').then((module) => ({
      default: module.UpdatesPage
    }))
)
const Agents = React.lazy(
  async () =>
    await import('./pages/agents').then((module) => ({
      default: module.AgentsPage
    }))
)

const loginWithoutQueryOrHash = (): string => {
  const url: string = LoginSuccessPage.login.success.url.toString()
  return url.replace(/\?.*$/, '').replace(/#.*$/, '')
}

const routerPaths = [
  {
    path: loginWithoutQueryOrHash(),
    element: <Navigate to={DefaultPage} replace={true} />,
    errorElement: <ErrorPage />
  },
  {
    path: LoginPage.login.url.toString(),
    element: <PageLogin />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyFancyLoginPage,
    element: <Navigate to={LoginPage.login.url.toString()} replace={true} />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginSuccess,
    element: (
      <Navigate
        to={LoginSuccessPage.login.success.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginFailed,
    element: (
      <Navigate
        to={LoginFailedPage.login.failed.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: Root.root.url.toString(),
    element: <Dashboard />,
    loader: () => ensureAuthenticated(version, LoginPage, Root),
    errorElement: <ErrorPage />,
    children: [
      {
        path: Root.root.url.toString(),
        element: <Navigate to={DefaultPage} replace={true} />,
        errorElement: <ErrorPage />
      },
      {
        path: MachinesRoute,
        element: <Machines />,
        errorElement: <ErrorPage />
      },
      {
        path: MachineDetailRoute,
        element: <MachineDetail />,
        errorElement: <ErrorPage />
      },
      {
        path: DisksRoute,
        element: <MachinesDisks />,
        errorElement: <ErrorPage />
      },
      {
        path: ApplyRoute,
        element: <Apply />,
        errorElement: <ErrorPage />
      },
      {
        path: UpdatesRoute,
        element: <Updates />,
        errorElement: <ErrorPage />
      },
      {
        path: AgentsRoute,
        element: <Agents />,
        errorElement: <ErrorPage />
      }
    ]
  }
]

printRouterTree(routerPaths)

const router = createBrowserRouter(routerPaths)

export const App: React.FC = () => <RouterProvider router={router} />
